import { useEffect, useRef } from 'react';
import { focusables, trapTab } from '../../utils/focusTrap';
import { reportTab } from '../../utils/issueReports';

/*
 * What makes an overlay modal, shared by components/ui/Dialog and its Sheet
 * variant (2026-10-21; it was Dialog's own since 2026-10-18):
 *
 *   - focus goes in when it opens -- to `initialFocusRef`, else the first
 *     thing in it that takes focus (but `skipRef`, the x), else the panel --
 *     stays in while Tab cycles, and goes back to whatever had it when it
 *     closes;
 *   - Escape calls onClose, unless `busy` holds it open.
 *
 * `trap` false lets Tab leave it: a non-modal sheet beside a page that is
 * still in use (the camera's controls sheet, Sheet.js `modal`).
 *
 * `escape` false leaves Escape to the caller, which then asks isInnermost()
 * before acting on it: a full-screen workbench over a page (the analysis
 * workbench's overlay variant, components/workbench/WorkbenchShell.js) whose
 * Escape first closes a drawer, and leaves a stroke on the canvas alone.
 *
 * Escape is taken from the document in the capture phase and only by the
 * innermost open one, so a confirmation over a sheet closes the confirmation,
 * not the sheet beneath it. Dialogs and sheets share the one stack.
 */

// The open dialogs and sheets, in the order they opened.
const stack = [];

/** Whether any dialog or sheet is open now (the page swipe reads nothing then). */
export function anyModalOpen() {
  return stack.some((entry) => entry.panel?.isConnected);
}

// The one Escape and Tab belong to: an overlay with no other open overlay
// inside it, the latest of those. Not simply the last to open: React runs a
// child's effects before its parent's, so a dialog rendered inside another
// that opens with it would otherwise register first.
function innermost() {
  const open = stack.filter((entry) => entry.panel?.isConnected);
  const leaves = open.filter((a) => !open.some((b) => b !== a && a.panel.contains(b.panel)));
  return leaves[leaves.length - 1];
}

/** Whether `panel` is the open overlay Escape and Tab belong to now (no other open inside it or after it). */
export function isInnermost(panel) {
  return !!panel && innermost()?.panel === panel;
}

export default function useModal(panelRef, {
  onClose, busy = false, initialFocusRef, skipRef, trap = true, escape = true,
} = {}) {
  const latest = useRef({ onClose, busy, trap, escape });
  latest.current = { onClose, busy, trap, escape };

  useEffect(() => {
    const panel = panelRef.current;
    const token = { panel };
    stack.push(token);
    const opener = document.activeElement;
    // Unless an overlay inside this one, opening with it, already took focus.
    if (!panel.contains(document.activeElement)) {
      const first = initialFocusRef?.current
        || focusables(panel).find((el) => el !== skipRef?.current)
        || panel;
      first?.focus?.({ preventScroll: true });
    }

    const onKey = (e) => {
      if (innermost() !== token || e.isComposing) return;
      if (e.key === 'Escape') {
        if (!latest.current.escape) return;
        e.preventDefault();
        e.stopImmediatePropagation();
        if (!latest.current.busy) latest.current.onClose?.();
      } else if (e.key === 'Tab' && latest.current.trap) {
        trapTab(panelRef.current, e, { extra: reportTab() });
      }
    };
    document.addEventListener('keydown', onKey, true);
    return () => {
      document.removeEventListener('keydown', onKey, true);
      const at = stack.indexOf(token);
      if (at !== -1) stack.splice(at, 1);
      if (opener && opener !== document.body && opener.isConnected !== false
          && typeof opener.focus === 'function') {
        opener.focus({ preventScroll: true });
      }
    };
    // Focus moves in once, as it opens; a re-render must not pull it back.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
}
